// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The validation overlay on the bench, in Three (M13-17).
//
// `validationView.ts` says what the report looks like on the machine; this draws it under
// `MachineScene.playfield`, in the playfield's own frame: the reachability map as a decal on the
// surface (one `DataTexture` from `decalPixels`, the same bytes the native client uploads), a
// callout over every part a complaint names — a disc the size of a fingertip in the *room*,
// whatever the bench's scale, with the Builder's short label on it — and, for a flipper pair at
// fault, the measurement: a line between the resting tips with the gap in millimetres over it.
//
// **Rebuilt on the view's key, not per frame.** The report changes when the definition does —
// on a release, an undo — and the picture is rebuilt then; between, `update` only re-sizes the
// callouts and labels for a bench scale that changed. The decal is re-uploaded when the map
// object changes, which is every rebuild, at cols × rows bytes × 4: NEON NOVA's ~200 × 430
// grid is a third of a megabyte, once per edit.
//
// Amber for a warning, the Builder's red for an error, on both clients; labels on canvases where
// there is one, as the wrist panel does, and plain discs in a build without.

import {
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  CircleGeometry,
  DataTexture,
  Group,
  Line,
  LineBasicMaterial,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  NearestFilter,
  PlaneGeometry,
  RGBAFormat,
  RingGeometry,
  SRGBColorSpace,
  Sprite,
  SpriteMaterial,
  UnsignedByteType,
} from 'three'
import { CALLOUT_FACE, CALLOUT_HEIGHT } from '../xr/build/calloutTaps'
import { calloutKey, decalPixels, type Callout, type Measurement, type ValidationView } from '../xr/build/validationView'
import type { ReachabilityMap } from '../../core/ts/machine/geometry'

/** A warning's amber and an error's red: the Builder's `trap` and `unreachable` at full strength. */
export const WARNING_COLOR = 0xffb020
export const ERROR_COLOR = 0xff4a60
/** Lift off the surface, playfield metres: over the paint, under a ball. */
const DECAL_LIFT = 0.0015
const MEASURE_LIFT = 0.004
const LABEL_CANVAS = { width: 256, height: 64 }
/** A callout's label, room metres: four to one, over the disc. */
const LABEL_WIDTH = 0.06
const LABEL_HEIGHT = 0.015
const LABEL_ABOVE = 0.012

export interface ValidationOverlayBuild {
  readonly group: Group
  /** This frame's view, or nothing, and the scale the bench is drawn at. */
  update(view: ValidationView | undefined, scale: number): void
  /** The callouts drawn, by key, for the taps. */
  readonly callouts: readonly { readonly key: string; readonly callout: Callout }[]
  dispose(): void
}

interface Labelled {
  readonly sprite: Sprite
  readonly material: SpriteMaterial
  readonly texture: CanvasTexture | undefined
  readonly canvas: HTMLCanvasElement | undefined
}

function makeLabel(): Labelled {
  const host = (globalThis as { document?: { createElement: (tag: string) => HTMLCanvasElement } }).document
  const canvas = host?.createElement('canvas')
  if (canvas !== undefined) {
    canvas.width = LABEL_CANVAS.width
    canvas.height = LABEL_CANVAS.height
  }
  const texture = canvas === undefined ? undefined : new CanvasTexture(canvas)
  if (texture !== undefined) {
    texture.colorSpace = SRGBColorSpace
    texture.generateMipmaps = false
    texture.minFilter = LinearFilter
  }
  const material = new SpriteMaterial({
    ...(texture === undefined ? { color: 0xffffff } : { map: texture }),
    transparent: true,
    depthTest: false,
    depthWrite: false,
  })
  const sprite = new Sprite(material)
  sprite.renderOrder = 1002
  return { sprite, material, texture, canvas }
}

function paintLabel(label: Labelled, text: string, colour: string): void {
  const { canvas, texture } = label
  if (canvas === undefined || texture === undefined) return
  const ctx = canvas.getContext('2d')
  if (ctx === null) return
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = 'rgba(12, 10, 20, 0.82)'
  ctx.fillRect(0, 0, canvas.width, canvas.height)
  ctx.fillStyle = colour
  ctx.font = '600 32px system-ui, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(text, canvas.width / 2, canvas.height / 2, canvas.width - 12)
  texture.needsUpdate = true
}

export function buildValidationOverlay(): ValidationOverlayBuild {
  const group = new Group()
  group.name = 'build.validation'
  group.visible = false

  // --- the decal ----------------------------------------------------------------------------
  let decal: { mesh: Mesh; geometry: PlaneGeometry; material: MeshBasicMaterial; texture: DataTexture; map: ReachabilityMap } | undefined
  const dropDecal = (): void => {
    if (decal === undefined) return
    group.remove(decal.mesh)
    decal.geometry.dispose()
    decal.material.dispose()
    decal.texture.dispose()
    decal = undefined
  }
  const showDecal = (map: ReachabilityMap | undefined): void => {
    if (map === undefined) {
      dropDecal()
      return
    }
    if (decal !== undefined && decal.map === map) return
    dropDecal()
    const pixels = decalPixels(map)
    const texture = new DataTexture(pixels.rgba, pixels.width, pixels.height, RGBAFormat, UnsignedByteType)
    texture.magFilter = NearestFilter
    texture.minFilter = NearestFilter
    texture.colorSpace = SRGBColorSpace
    texture.needsUpdate = true
    const width = map.cols * map.cell
    const height = map.rows * map.cell
    const geometry = new PlaneGeometry(width, height)
    const material = new MeshBasicMaterial({ map: texture, transparent: true, depthWrite: false })
    const mesh = new Mesh(geometry, material)
    mesh.name = 'validation-decal'
    mesh.position.set(map.minX + width / 2, map.minY + height / 2, DECAL_LIFT)
    mesh.renderOrder = 5
    group.add(mesh)
    decal = { mesh, geometry, material, texture, map }
  }

  // --- the callouts -------------------------------------------------------------------------
  const disc = new CircleGeometry(CALLOUT_FACE / 2, 24)
  const rim = new RingGeometry(CALLOUT_FACE / 2, CALLOUT_FACE / 2 + 0.003, 24)
  interface Drawn {
    readonly key: string
    readonly callout: Callout
    readonly pivot: Group
    readonly discMaterial: MeshBasicMaterial
    readonly rimMaterial: MeshBasicMaterial
    readonly label: Labelled
  }
  let drawn: Drawn[] = []
  const dropCallouts = (): void => {
    for (const d of drawn) {
      group.remove(d.pivot)
      d.discMaterial.dispose()
      d.rimMaterial.dispose()
      d.label.material.dispose()
      d.label.texture?.dispose()
    }
    drawn = []
  }
  const showCallouts = (callouts: readonly Callout[]): void => {
    dropCallouts()
    drawn = callouts.map((callout) => {
      const colour = callout.severity === 'error' ? ERROR_COLOR : WARNING_COLOR
      const pivot = new Group()
      pivot.name = `callout-${calloutKey(callout)}`
      const discMaterial = new MeshBasicMaterial({ color: 0x0c0a14, transparent: true, opacity: 0.85, depthTest: false, depthWrite: false })
      const rimMaterial = new MeshBasicMaterial({ color: colour, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false })
      const face = new Mesh(disc, discMaterial)
      face.renderOrder = 1001
      const ring = new Mesh(rim, rimMaterial)
      ring.renderOrder = 1001
      pivot.add(face)
      pivot.add(ring)
      const label = makeLabel()
      label.sprite.scale.set(LABEL_WIDTH, LABEL_HEIGHT, 1)
      label.sprite.position.set(0, LABEL_ABOVE, 0.002)
      paintLabel(label, callout.label, callout.severity === 'error' ? '#ff4a60' : '#ffb020')
      pivot.add(label.sprite)
      group.add(pivot)
      return { key: calloutKey(callout), callout, pivot, discMaterial, rimMaterial, label }
    })
  }

  // --- the measurements ---------------------------------------------------------------------
  interface Measured {
    readonly line: Line
    readonly geometry: BufferGeometry
    readonly material: LineBasicMaterial
    readonly label: Labelled
  }
  let measured: Measured[] = []
  const dropMeasures = (): void => {
    for (const m of measured) {
      group.remove(m.line)
      group.remove(m.label.sprite)
      m.geometry.dispose()
      m.material.dispose()
      m.label.material.dispose()
      m.label.texture?.dispose()
    }
    measured = []
  }
  const showMeasures = (measures: readonly Measurement[]): void => {
    dropMeasures()
    measured = measures.map((m) => {
      const colour = m.severity === 'error' ? ERROR_COLOR : WARNING_COLOR
      const geometry = new BufferGeometry()
      geometry.setAttribute(
        'position',
        new BufferAttribute(new Float32Array([m.from[0], m.from[1], MEASURE_LIFT, m.to[0], m.to[1], MEASURE_LIFT]), 3),
      )
      const material = new LineBasicMaterial({ color: colour, transparent: true, opacity: 0.95, depthTest: false, depthWrite: false })
      const line = new Line(geometry, material)
      line.name = `measure-${m.leftId}-${m.rightId}`
      line.renderOrder = 1001
      line.frustumCulled = false
      group.add(line)
      const label = makeLabel()
      label.sprite.name = `measure-label-${m.leftId}-${m.rightId}`
      paintLabel(label, m.text, m.severity === 'error' ? '#ff4a60' : '#ffb020')
      group.add(label.sprite)
      return { line, geometry, material, label }
    })
  }

  let keyNow: string | undefined
  let viewNow: ValidationView | undefined

  return {
    group,
    get callouts() {
      return drawn.map((d) => ({ key: d.key, callout: d.callout }))
    },
    update(view, scale) {
      if (view === undefined) {
        if (viewNow !== undefined) {
          dropDecal()
          dropCallouts()
          dropMeasures()
        }
        viewNow = undefined
        keyNow = undefined
        group.visible = false
        return
      }
      group.visible = true
      if (view.key !== keyNow) {
        keyNow = view.key
        showCallouts(view.callouts)
        showMeasures(view.measures)
      }
      showDecal(view.map)
      viewNow = view
      // Room-sized whatever the bench's scale: the faces the taps measure against.
      const inverse = scale > 0 ? 1 / scale : 1
      const height = CALLOUT_HEIGHT * inverse
      for (const d of drawn) {
        // A ramp's callout hangs the same hand's height over the ramp, up where the ramp is (M13-24).
        d.pivot.position.set(d.callout.at[0], d.callout.at[1], height + d.callout.lift)
        d.pivot.scale.set(inverse, inverse, inverse)
      }
      measured.forEach((m, i) => {
        const source = view.measures[i]
        if (source === undefined) return
        m.label.sprite.scale.set(LABEL_WIDTH * inverse, LABEL_HEIGHT * inverse, 1)
        m.label.sprite.position.set(
          (source.from[0] + source.to[0]) / 2,
          (source.from[1] + source.to[1]) / 2 + LABEL_ABOVE * inverse,
          MEASURE_LIFT + 0.002,
        )
      })
    },
    dispose() {
      dropDecal()
      dropCallouts()
      dropMeasures()
      disc.dispose()
      rim.dispose()
    },
  }
}
